'use client';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';

import Icon from '@/components/ui/AppIcon';
import StepPersonal from './StepPersonal';
import StepBank from './StepBank';
import StepPayment from './StepPayment';

export interface SignUpFormData {
  // Step 1
  fullName: string;
  email: string;
  confirmEmail: string;
  phone: string;
  // Step 2
  bankName: string;
  otherBankName: string;
  accountHolderName: string;
  accountNumber: string;
  confirmAccountNumber: string;
  bankBranch: string;
  accountType: 'checking' | 'savings';
}

const STEPS = [
  { id: 'step-personal', label: 'Personal Info', number: 1 },
  { id: 'step-bank', label: 'Bank Details', number: 2 },
  { id: 'step-payment', label: 'Payment', number: 3 },
];

export default function SignUpFlow() {
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState<Partial<SignUpFormData>>({});

  const form = useForm<SignUpFormData>({ mode: 'onBlur' });

  const goNext = (data: Partial<SignUpFormData>) => {
    setFormData((prev) => ({ ...prev, ...data }));
    setCurrentStep((s) => Math.min(s + 1, 3));
  };

  const goBack = () => setCurrentStep((s) => Math.max(s - 1, 1));

  return (
    <div>
      {/* Progress indicator */}
      <div className="mb-10">
        <div className="flex items-center gap-0 mb-6">
          {STEPS.map((step, idx) => (
            <React.Fragment key={step.id}>
              <div className="flex flex-col items-center">
                <div
                  className={`w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold transition-all duration-200 ${
                    currentStep > step.number
                      ? 'bg-[#10B981] text-white'
                      : currentStep === step.number
                      ? 'bg-[#F59E0B] text-[#0F0F0F]'
                      : 'bg-[#2A2A2A] text-[#6B7280]'
                  }`}
                >
                  {currentStep > step.number ? (
                    <Icon name="CheckIcon" size={16} />
                  ) : (
                    step.number
                  )}
                </div>
                <p
                  className={`text-xs mt-2 font-medium transition-colors ${
                    currentStep === step.number ? 'text-[#F59E0B]' : 'text-[#6B7280]'
                  }`}
                >
                  {step.label}
                </p>
              </div>
              {idx < STEPS.length - 1 && (
                <div
                  className={`flex-1 h-px mx-2 mb-5 transition-colors duration-300 ${
                    currentStep > step.number ? 'bg-[#10B981]' : 'bg-[#2A2A2A]'
                  }`}
                />
              )}
            </React.Fragment>
          ))}
        </div>
      </div>

      {/* Step content */}
      <div className="animate-slide-up">
        {currentStep === 1 && (
          <StepPersonal form={form} formData={formData} onNext={goNext} />
        )}
        {currentStep === 2 && (
          <StepBank form={form} formData={formData} onNext={goNext} onBack={goBack} />
        )}
        {currentStep === 3 && (
          <StepPayment formData={formData} onBack={goBack} />
        )}
      </div>
    </div>
  );
}